import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import ImageUpload from './ImageUpload';
import EnhancedRichTextEditor from './EnhancedRichTextEditor';

const ChairmanMessageEdit = () => {
  const [chairmanMessageData, setChairmanMessageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    bannerTitle: '',
    bannerSubtitle: '',
    bannerImage: '',
    chairmanName: '',
    chairmanTitle: '',
    chairmanInstitution: '',
    chairmanImage: '',
    messageContent: ''
  });

  useEffect(() => {
    fetchChairmanMessageData();
  }, []);

  const fetchChairmanMessageData = async () => {
    try {
      setLoading(true);
      const response = await apiService.getChairmanMessage();
      const data = response.data;
      
      setChairmanMessageData(data);
      setFormData({
        bannerTitle: data.bannerTitle || '',
        bannerSubtitle: data.bannerSubtitle || '',
        bannerImage: data.bannerImage || '',
        chairmanName: data.chairmanName || '',
        chairmanTitle: data.chairmanTitle || '',
        chairmanInstitution: data.chairmanInstitution || '',
        chairmanImage: data.chairmanImage || '',
        messageContent: data.messageContent || ''
      });
    } catch (error) {
      setError('Failed to load chairman message data');
      console.error('Error fetching chairman message:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      if (chairmanMessageData?.id) {
        await apiService.updateChairmanMessage(chairmanMessageData.id, formData);
        setSuccess('Chairman Message updated successfully!');
      } else {
        await apiService.createChairmanMessage(formData);
        setSuccess('Chairman Message created successfully!');
      }
      
      // Refresh data
      await fetchChairmanMessageData();
    } catch (error) {
      setError('Failed to save chairman message data');
      console.error('Error saving chairman message:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-slate">
          <i className="fas fa-spinner fa-spin mr-2 text-crimson"></i>
          Loading chairman message data...
        </div>
      </div>
    );
  }

  return (
    <div className="font-sans">
      <div className="mb-8">
        <h1 className="text-3xl font-sans font-bold text-slate mb-2">
          Chairman's Message Management
        </h1>
        <p className="text-gray-600">
          Manage the content for the Chairman's Message page including banner, chairman information, and message content.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          <i className="fas fa-exclamation-circle mr-2"></i>
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6">
          <i className="fas fa-check-circle mr-2"></i>
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Banner Section */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Banner Section</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate mb-2">
                Banner Title
              </label>
              <input
                type="text"
                value={formData.bannerTitle}
                onChange={(e) => handleInputChange('bannerTitle', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                placeholder="Chairman's Message"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">
                Banner Subtitle
              </label>
              <textarea
                value={formData.bannerSubtitle}
                onChange={(e) => handleInputChange('bannerSubtitle', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                rows={2}
                placeholder="Leading with vision, inspiring excellence in education and innovation"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">
                Banner Image
              </label>
              <ImageUpload
                value={formData.bannerImage}
                onChange={(value) => handleInputChange('bannerImage', value)}
                placeholder="Banner background image filename"
              />
            </div>
          </div>
        </div>

        {/* Chairman Information Section */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Chairman Information</h2>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate mb-2">
                  Chairman Name
                </label>
                <input
                  type="text"
                  value={formData.chairmanName}
                  onChange={(e) => handleInputChange('chairmanName', e.target.value)}
                  className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                  placeholder="Dr. Rajesh Kumar"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate mb-2">
                  Chairman Title
                </label>
                <input
                  type="text"
                  value={formData.chairmanTitle}
                  onChange={(e) => handleInputChange('chairmanTitle', e.target.value)}
                  className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                  placeholder="Chairman"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">
                Institution Name
              </label>
              <input
                type="text"
                value={formData.chairmanInstitution}
                onChange={(e) => handleInputChange('chairmanInstitution', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                placeholder="FiT Engineering College"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">
                Chairman Photo
              </label>
              <ImageUpload
                value={formData.chairmanImage}
                onChange={(value) => handleInputChange('chairmanImage', value)}
                placeholder="Chairman's photo filename"
              />
            </div>
          </div>
        </div>

        {/* Message Content Section */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Message Content</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate mb-2">
                Chairman's Message Content
              </label>
              <EnhancedRichTextEditor
                value={formData.messageContent}
                onChange={(value) => handleInputChange('messageContent', value)}
                placeholder="Enter the chairman's message here..."
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-3 bg-crimson text-white rounded-lg font-medium hover:bg-crimson/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {saving ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Saving...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                Save Chairman's Message
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChairmanMessageEdit;